/**
 * BoardHeader — consolidated board page header.
 *
 * Single header zone replaces the old 3-bar stack (header + board info + stats).
 * Two rows:
 *   Row 1: Board selector + board title/description + sprint badge
 *   Row 2: View switcher + stat pills + display + actions
 */

import { useMemo } from 'react';
import type { Board, Issue } from '../../../api/work';
import type { ViewMode } from '../../../utils/preferences';
import type { UseBoardDisplayReturn } from '../../../hooks/useBoardDisplay';
import { ViewSwitcher } from '../navigation/ViewSwitcher';
import { DisplayPopover } from '../shared/DisplayPopover';
import { announce } from '../../../utils/announcer';

interface BoardHeaderProps {
  boards: Board[];
  selectedBoardId: string;
  board: Board | null;
  viewMode: ViewMode;
  display: UseBoardDisplayReturn;
  issues: Record<string, Issue>;
  initiativesCount: number;
  isMobile: boolean;
  projectId: string | undefined;
  onBoardChange: (boardId: string) => void;
  onViewChange: (view: ViewMode) => void;
  onCreateIssue: () => void;
  onCreateBoard: () => void;
  onRefresh: () => void;
}

function StatPill({ label, count, color }: { label: string; count: number; color: string }) {
  if (count === 0) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium tabular-nums"
      style={{ backgroundColor: `${color}12`, color }}
    >
      <span className="font-semibold">{count}</span>
      <span className="opacity-70">{label}</span>
    </span>
  );
}

export function BoardHeader({
  boards,
  selectedBoardId,
  board,
  viewMode,
  display,
  issues,
  initiativesCount,
  isMobile,
  projectId,
  onBoardChange,
  onViewChange,
  onCreateIssue,
  onCreateBoard,
  onRefresh,
}: BoardHeaderProps) {
  const currentBoard = boards.find(b => b.id === selectedBoardId);

  const stats = useMemo(() => {
    const values = Object.values(issues);
    return {
      stories: values.filter(i => i.type === 'story').length,
      tasks: values.filter(i => i.type === 'task').length,
      bugs: values.filter(i => i.type === 'bug').length,
    };
  }, [issues]);

  const totalIssues = Object.keys(issues).length;

  return (
    <header
      className="border-b"
      style={{ borderBottomColor: '#E8E0D5' }}
      role="banner"
    >
      {/* Row 1: Board identity */}
      <div
        className="px-5 md:px-6 pt-4 pb-3"
        style={{ backgroundColor: 'white' }}
      >
        <div className="flex items-start justify-between gap-4">
          {/* Left: board name + meta */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2.5">
              {/* Board selector + new-board button */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {boards.length > 1 && (
                  <div className="relative">
                    <label htmlFor="board-selector" className="sr-only">Select board</label>
                    <select
                      id="board-selector"
                      data-testid="board-selector"
                      value={selectedBoardId}
                      onChange={(e) => {
                        onBoardChange(e.target.value);
                        announce(`Switched to ${boards.find(b => b.id === e.target.value)?.name || e.target.value} board`);
                      }}
                      className="appearance-none border rounded-md pl-2.5 pr-7 py-1 text-xs font-medium focus:outline-none cursor-pointer transition-colors hover:border-[#C4B8A9]"
                      style={{ backgroundColor: '#F5F1EC', borderColor: '#E8E0D5', color: '#6B5D52' }}
                      aria-label="Select board"
                    >
                      {boards.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                    <svg
                      className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none w-3 h-3"
                      style={{ color: '#A39686' }}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                )}
                <button
                  onClick={onCreateBoard}
                  className="p-1 rounded-md transition-colors hover:bg-[#F5F1EC]"
                  style={{ color: '#A39686' }}
                  title="Create new board"
                  aria-label="Create new board"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>

              <h1
                className="text-lg font-semibold tracking-tight truncate"
                style={{ color: '#2F241B', fontFamily: "'Source Serif 4', Georgia, serif" }}
              >
                {currentBoard?.name || 'Board'}
              </h1>

              {/* Live dot — subtle, no badge */}
              <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: '#10B981' }} title="Live" />
            </div>

            {currentBoard?.description && (
              <p className="text-xs mt-0.5 truncate" style={{ color: '#A39686' }}>
                {currentBoard.description}
              </p>
            )}
          </div>

          {/* Right: sprint badge + new issue */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {board?.sprint && (
              <div
                className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs"
                style={{ backgroundColor: '#F5F1EC', color: '#6B5D52' }}
              >
                <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="font-medium">{board.sprint.name}</span>
              </div>
            )}

            <button
              onClick={onCreateIssue}
              className="px-3 py-1.5 rounded-md text-white text-xs font-semibold transition-all hover:brightness-110 active:scale-[0.97] touch-manipulation"
              style={{ backgroundColor: '#D97F6F', minHeight: isMobile ? '44px' : 'auto' }}
              aria-label="Create new issue"
            >
              <span className="hidden md:inline">+ New Issue</span>
              <span className="md:hidden text-base leading-none">+</span>
            </button>
          </div>
        </div>
      </div>

      {/* Row 2: toolbar — view switcher, stats, display, actions */}
      <div
        className="flex items-center justify-between gap-3 px-5 md:px-6 py-2"
        style={{ backgroundColor: '#FDFCFA' }}
      >
        {/* Left: view switcher + stat pills */}
        <div className="flex items-center gap-3 min-w-0">
          {!isMobile && <ViewSwitcher currentView={viewMode} onViewChange={onViewChange} />}

          {/* Stat pills — compact, colored, scannable */}
          {!isMobile && (
            <div className="flex items-center gap-1.5">
              <StatPill label="epics" count={initiativesCount} color="#7C5CFC" />
              <StatPill label="stories" count={stats.stories} color="#2F8ADB" />
              <StatPill label="tasks" count={stats.tasks} color="#6B5D52" />
              <StatPill label="bugs" count={stats.bugs} color="#E5534B" />

              {totalIssues > 0 && (
                <span className="text-[11px] ml-1 tabular-nums" style={{ color: '#A39686' }}>
                  {totalIssues} total
                </span>
              )}
            </div>
          )}
        </div>

        {/* Right: display options, refresh */}
        <div className="flex items-center gap-1.5">
          {!isMobile && projectId && <DisplayPopover display={display} />}

          <button
            onClick={onRefresh}
            className="p-1.5 rounded-md transition-colors hover:bg-[#F5F1EC]"
            style={{ color: '#A39686' }}
            title="Refresh board"
            aria-label="Refresh board"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>
    </header>
  );
}
